import Header from "../../components/Header/Header";
import { StatsUtilizationSection } from "./components/StatsUtilizationSection/StatsUtilizationSection";
import { EscalationContactSection } from "./components/EscalationContactSection/EscalationContactSection";
import { ModalitiesSection } from "./components/ModalitiesSection";
import { FeatureShowcaseSection } from "./components/FeatureShowcaseSection";
import { MoreForYouSection } from "./components/SupportSection";
import { KamShimmer } from "./components/KamShimmer";
import UserManagementIcon from "../../assets/svg/user-management-icon";
import PropertyManagementIcon from "../../assets/svg/property-management-icon";
import CrmDashboardIcon from "../../assets/svg/crm-dashboard-icon";
import VerbachatIcon from "../../assets/svg/verbachat-icon";
import VerbaCallIcon from "../../assets/svg/verbacall-icon";
import VerbamessengerIcon from "../../assets/svg/verbamessenger-icon";
import {
  useGetEscalationContactSectionDataQuery,
  useGetKamAnalyticsQuery,
  useGetKamFeaturesQuery,
  useGetKamMoreForYouQuery,
  useGetKamModalitiesQuery,
} from "../../redux/services/kamApi";
import { useGetClientsQuery } from "../../redux/services/organisationApi";
import {
  FeatureCardData,
  ModalityCardData,
  StatCardProps,
  UtilizationData,
} from "./utils/types";
import { useMemo, useState } from "react";
import {
  extractOrganizationClients,
  formatFeatureTag,
  formatModalityTag,
  getFeatureIcon,
  getFeatureImage,
  getModalityIcon,
  getModalityIconColor,
} from "./utils/kamHelpers";

type OrganizationClient = {
  client_code: string;
  client_name: string;
  client_logo: string | null;
};

type OrganizationItem = {
  clients?: OrganizationClient[];
};

export const Kam = () => {
  const [isOrganizationsModalOpen, setIsOrganizationsModalOpen] = useState(false);
  const [isGetMoreModalitiesModalOpen, setIsGetMoreModalitiesModalOpen] =
    useState(false);
  const [isGetMoreFeaturesModalOpen, setIsGetMoreFeaturesModalOpen] =
    useState(false);
  const { data: kamAnalyticsData, isLoading: isAnalyticsLoading } =
    useGetKamAnalyticsQuery();
  const {
    data: escalationContactSectionData,
    isLoading: isEscalationContactLoading,
  } = useGetEscalationContactSectionDataQuery();
  const { data: kamModalitiesData, isLoading: isModalitiesLoading } =
    useGetKamModalitiesQuery();
  const { data: kamFeaturesData, isLoading: isFeaturesLoading } =
    useGetKamFeaturesQuery();
  const { data: kamMoreForYouData, isLoading: isMoreForYouLoading } =
    useGetKamMoreForYouQuery();
  const {
    data: organizationsData,
    isLoading: isOrganizationsLoading,
    isFetching: isOrganizationsFetching,
  } = useGetClientsQuery(undefined, {
    skip: !isOrganizationsModalOpen,
  });
  const isLoading =
    isAnalyticsLoading ||
    isEscalationContactLoading ||
    isModalitiesLoading ||
    isFeaturesLoading ||
    isMoreForYouLoading;
  const hasKamAnalyticsData = Boolean(kamAnalyticsData);
  const utilizationUsedValue =
    typeof kamAnalyticsData?.product_utilisation_percentage === "number"
      ? Math.round(kamAnalyticsData.product_utilisation_percentage)
      : 0;

  const handleOpenOrganizationsModal = () => {
    setIsOrganizationsModalOpen(true);
  };

  const handleOrganizationsModalOpenChange = (open: boolean) => {
    setIsOrganizationsModalOpen(open);
  };

  const handleOpenGetMoreModalitiesModal = () => {
    setIsGetMoreModalitiesModalOpen(true);
  };

  const handleOpenGetMoreFeaturesModal = () => {
    setIsGetMoreFeaturesModalOpen(true);
  };

  const handleGetMoreModalitiesModalOpenChange = (open: boolean) => {
    setIsGetMoreModalitiesModalOpen(open);
  };

  const handleGetMoreFeaturesModalOpenChange = (open: boolean) => {
    setIsGetMoreFeaturesModalOpen(open);
  };

  const organizationClients = useMemo(() => {
    if (!organizationsData) {
      return [];
    }

    const payload = organizationsData as {
      data?: OrganizationItem | OrganizationItem[];
      results?: OrganizationItem | OrganizationItem[];
    } & (OrganizationItem | OrganizationItem[]);

    return extractOrganizationClients(payload.data || payload.results || payload);
  }, [organizationsData]);

  const statsData: StatCardProps[] = [
    {
      IconComponent: UserManagementIcon,
      iconColor: "w-6 h-6 text-verba-blue dark:text-verba-blue-dk",
      value: hasKamAnalyticsData ? (kamAnalyticsData?.total_users ?? "-") : "-",
      label: "Total Users",
      actionText: "View >",
    },
    {
      IconComponent: PropertyManagementIcon,
      iconColor:
        "w-6 h-6 text-verba-green-success dark:text-verba-green-success-dk",
      value: hasKamAnalyticsData
        ? (kamAnalyticsData?.total_organisations ?? "-")
        : "-",
      label: "Total Organisations",
      actionText: "View All >",
    },
    // {
    //   IconComponent: WorkflowIcon,
    //   iconColor:
    //     "w-6 h-6 text-tasks-scheduled dark:text-tasks-scheduled-dk",
    //   value: "13",
    //   outOf: "48",
    //   label: "WorkFlo Use Cases",
    //   actionText: "Get More >",
    // },
    {
      IconComponent: CrmDashboardIcon,
      iconColor: "w-6 h-6 text-tasks-scheduled dark:text-tasks-scheduled-dk",
      value: hasKamAnalyticsData
        ? (kamAnalyticsData?.features_live_count ?? "-")
        : "-",
      outOf: hasKamAnalyticsData
        ? (kamAnalyticsData?.features_total_count ?? "-")
        : "-",
      label: "Features Live",
      actionText: "Discover >",
      percentage: kamAnalyticsData?.features_total_count
        ? Math.round(
            (kamAnalyticsData.features_live_count /
              kamAnalyticsData.features_total_count) *
              100
          )
        : undefined,
      percentageColor:
        "bg-tasks-scheduled-bg dark:bg-tasks-scheduled-bg-dk text-tasks-scheduled dark:text-tasks-scheduled-dk",
      onClick: handleOpenGetMoreFeaturesModal,
    },
    {
      IconComponent: [
        {
          icon: VerbachatIcon,
          iconColor: "w-3.5 h-3.5 text-verba-blue dark:text-verba-blue-dk",
          bgColor: "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        },
        {
          icon: VerbaCallIcon,
          iconColor:
            "w-3.5 h-3.5 text-verba-lavender dark:text-verba-lavender-dk",
          bgColor: "bg-tasks-scheduled-bg dark:bg-tasks-scheduled-bg-dk",
        },
        {
          icon: VerbamessengerIcon,
          iconColor:
            "w-3.5 h-3.5 text-verba-messenger dark:text-verba-messenger-dk",
          bgColor:
            "bg-user-management-green-card-bg dark:bg-user-management-green-card-bg-dk",
        },
      ],
      value: hasKamAnalyticsData
        ? (kamAnalyticsData?.modalities_live_count ?? "-")
        : "-",
      outOf: hasKamAnalyticsData
        ? (kamAnalyticsData?.modalities_total_count ?? "-")
        : "-",
      label: "Modalities Subscribed",
      actionText: "More >",
      onClick: handleOpenGetMoreModalitiesModal,
    },
  ];

  const utilizationData: UtilizationData = {
    used: utilizationUsedValue,
    total: hasKamAnalyticsData ? 100 : 0,
    title: "Product Utilization",
    description:
      "You’re only using a part of VerbaFlo AI’s capabilities. Explore more features and modalities to boost your usage.",
    ctaText: "Contact us >",
  };

  const modalitiesData: ModalityCardData[] = (kamModalitiesData?.modalities || []).map(
    (modality) => ({
      code: modality.code,
      icon: getModalityIcon(modality.code),
      iconColor: getModalityIconColor(modality.code),
      title: modality.title || "-",
      description: modality.description || "-",
      isLive: modality.is_live,
      badge: formatModalityTag(modality.tag)
        ? { text: formatModalityTag(modality.tag) as string }
        : undefined,
      ctaText: modality.title ? `Get ${modality.title}` : undefined,
    })
  );

  const featuresData: FeatureCardData[] = (kamFeaturesData?.features || []).map(
    (feature) => ({
      code: feature.code,
      title: feature.title || "-",
      description: feature.description || "-",
      image: getFeatureImage(feature.code),
      icon: getFeatureIcon(feature.code),
      isLive: feature.is_live,
      badge: formatFeatureTag(feature.tag),
      ctaText: feature.is_live
        ? `Explore ${feature.title.split(":")[0] || "Feature"}`
        : `Get ${feature.title.split(":")[0] || "Feature"}`,
    })
  );
  return (
    <div className="flex flex-col w-full h-full bg-pure-white dark:bg-pure-white-dk overflow-auto">
      <Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[8px]">
            <h1 className="text-xl font-semibold text-verba-indigo dark:text-verba-indigo-dk">
              Account Overview
            </h1>
          </div>
        </div>
      </Header>

      <div className="flex flex-col gap-12 pl-4 pr-4 pt-4 border-l border-t rounded-tl-[6px] border-border-gray dark:border-border-gray-dk">
        {isLoading ? (
          <KamShimmer />
        ) : (
          <>
            <StatsUtilizationSection
              statsData={statsData}
              utilizationData={utilizationData}
              totalUsersRedirectPath="/dashboard/user-management/all-users"
              onTotalOrganisationsClick={handleOpenOrganizationsModal}
              isOrganizationsModalOpen={isOrganizationsModalOpen}
              onOrganizationsModalOpenChange={handleOrganizationsModalOpenChange}
              organizations={organizationClients}
              isOrganizationsLoading={
                isOrganizationsModalOpen &&
                (isOrganizationsLoading || isOrganizationsFetching)
              }
            />
            <EscalationContactSection
              escalationContactSectionData={escalationContactSectionData}
            />
            <ModalitiesSection
              modalitiesData={modalitiesData}
              isRequestModalOpen={isGetMoreModalitiesModalOpen}
              onRequestModalOpenChange={handleGetMoreModalitiesModalOpenChange}
              submitApiType="modalities"
            />
            <FeatureShowcaseSection
              features={featuresData}
              isRequestModalOpen={isGetMoreFeaturesModalOpen}
              onRequestModalOpenChange={handleGetMoreFeaturesModalOpenChange}
              submitApiType="features"
            />
            {/* <GetMoreWorkfloSection workfloUseCasesData={workfloUseCasesData} /> */}
            <MoreForYouSection moreForYouItems={kamMoreForYouData?.items} />
          </>
        )}
      </div>
    </div>
  );
};
